import { Button, ButtonSize, ButtonStyle } from '@atlas/components';

import {
  getRealityCheckSettingsRequest,
  setRealityCheckSettingsRequest,
} from 'api/responsibleGaming';
import classNames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import Icon from 'components/Icon/Icon';
import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClientProfile } from 'store';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation, useTranslation } from 'utils/translations';
import { Period, realityCheckLimits } from 'utils/types';
import useLazyRequest from 'utils/useLazyRequest';

import styles from './styles.module.scss';

const RealityCheckSection: FC = () => {
  const [{ sessionToken }] = useClientProfile();
  const [period, setPeriod] = useState<Period>(Period.NEVER);
  const layout = useLayout();

  useEffect(() => {
    getRealityCheckSettingsRequest({
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    }).then(({ period }) => {
      if (period) {
        setPeriod(period);
      }
    });
  }, [sessionToken]);

  const { execute } = useLazyRequest(() =>
    setRealityCheckSettingsRequest({
      body: {
        period,
      },
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    }),
  );

  const handleChangePeriod = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setPeriod(event.currentTarget.dataset.value as Period);
    },
    [],
  );

  const handleSavePeriod = useCallback(() => {
    execute({ period });
  }, [execute, period]);

  const realityCheckTranslation = useTranslation('reminders', {
    time: realityCheckLimits[period].text,
  });
  const realityCheckText = useMemo(
    () => realityCheckTranslation.split(':'),
    [realityCheckTranslation],
  );

  return (
    <Collapsible className={styles.control}>
      <Collapsible.Trigger>
        {({ expanded }) => (
          <>
            <div className={styles['control-header']}>
              <Icon
                name="message-notif.bold"
                width={48}
                height={48}
                className={styles.icon}
              />
              <div className={styles['control-header-content']}>
                <div className={styles.title}>
                  <Translation message="about_reality_check" />
                </div>
                <p className={styles.description}>
                  <Translation message="reality_check_message" />
                </p>
              </div>
              <Icon
                name="arrow-right.circle"
                className={classNames(styles.arrow, {
                  [styles['arrow-expanded']]: expanded,
                })}
              />
            </div>
            {period !== Period.NEVER && !expanded && (
              <div
                className={classNames(styles.info, {
                  [styles['info-desktop']]: layout !== LayoutType.mobile,
                })}
              >
                <div className={styles['info-item']}>
                  {realityCheckText[0]}:{' '}
                  <span className={styles['info-item-value']}>
                    {realityCheckText[1]}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </Collapsible.Trigger>
      <Collapsible.Content>
        {({ toggle }) => (
          <>
            <div
              className={classNames(styles.buttons, {
                [styles['buttons-desktop']]: layout !== LayoutType.mobile,
              })}
            >
              {Object.values(realityCheckLimits).map((button) => (
                <Button
                  data-value={button.value}
                  onClick={handleChangePeriod}
                  key={button.value}
                  size={ButtonSize.small}
                  style={ButtonStyle.outline}
                  className={classNames(styles['buttons-item'], {
                    [styles.active]: button.value === period,
                  })}
                >
                  <Translation message={button.value.toLowerCase()} />
                </Button>
              ))}
            </div>
            <div className={styles['action-buttons']}>
              <Button
                onClick={() => {
                  handleSavePeriod();
                  toggle();
                }}
              >
                <Translation message="save" />
              </Button>
              <Button
                onClick={() => {
                  toggle();
                  setPeriod(Period.NEVER);
                }}
                style={ButtonStyle.outline}
              >
                <Translation message="cancel" />
              </Button>
            </div>
          </>
        )}
      </Collapsible.Content>
    </Collapsible>
  );
};

export default RealityCheckSection;
